import { ApiError, api, toApiError } from "./client";

export type AssistantActionStatus = "pending" | "executing" | "confirmed" | "cancelled" | "expired";

/** Resumo do cartão, montado pelo servidor com dados do banco. */
export interface ActionSummary {
  currency: string | null;
  accountName?: string;
  toAccountName?: string;
  categoryName?: string;
  parentCategoryName?: string;
  target?: { description: string; amountMinor: number; currency: string; date: string; accountName: string };
}

export interface AssistantAction {
  id: string;
  tool: string;
  status: AssistantActionStatus;
  input: Record<string, unknown>;
  summary: ActionSummary;
  expiresAt: number;
  result: unknown;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export type ChatEvent =
  | { type: "delta"; text: string }
  | { type: "proposal"; action: AssistantAction }
  | { type: "error"; code: string; message: string }
  | { type: "done" };

/** Leitor incremental de `text/event-stream`: recebe pedaços de texto e emite (evento, dados). */
export function createSseParser(onMessage: (event: string, data: string) => void) {
  let buffer = "";
  return (chunk: string) => {
    // Normaliza no buffer inteiro: um "\r\n" pode chegar partido entre dois pedaços.
    buffer = (buffer + chunk).replace(/\r\n/g, "\n");
    let sep = buffer.indexOf("\n\n");
    while (sep !== -1) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let event = "message";
      const data: string[] = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
      }
      if (data.length) onMessage(event, data.join("\n"));
      sep = buffer.indexOf("\n\n");
    }
  };
}

export function toChatEvent(event: string, data: string): ChatEvent | undefined {
  const parsed = JSON.parse(data);
  switch (event) {
    case "delta":
      return { type: "delta", text: String(parsed.text ?? "") };
    case "proposal":
      return { type: "proposal", action: parsed as AssistantAction };
    case "error":
      return { type: "error", code: String(parsed.code), message: String(parsed.message) };
    case "done":
      return { type: "done" };
    default:
      return undefined;
  }
}

/** Foto da mensagem (fase 2): a imagem já reduzida e reexportada, e a chave da NFC-e lida no QR. */
export interface ChatPhoto {
  blob: Blob;
  nfceKey?: string;
}

function chatBody(messages: ChatMessage[], photo?: ChatPhoto): { body: BodyInit; headers: HeadersInit } {
  if (!photo) {
    return {
      body: JSON.stringify({ messages }),
      headers: { "content-type": "application/json", accept: "text/event-stream" },
    };
  }
  // Multipart sem `content-type` fixo: o navegador põe o boundary.
  const form = new FormData();
  form.set("payload", JSON.stringify({ messages }));
  form.set("image", photo.blob, "foto.jpg");
  if (photo.nfceKey) form.set("nfceKey", photo.nfceKey);
  return { body: form, headers: { accept: "text/event-stream" } };
}

/** Envia a conversa (com foto, em multipart) e entrega os eventos à medida que chegam. Erro HTTP vira ApiError. */
export async function streamChat(
  wid: string,
  messages: ChatMessage[],
  onEvent: (e: ChatEvent) => void,
  signal?: AbortSignal,
  photo?: ChatPhoto,
) {
  let res: Response;
  try {
    res = await fetch(`/api/v1/workspaces/${wid}/assistant/chat`, {
      method: "POST",
      credentials: "same-origin",
      ...chatBody(messages, photo),
      signal,
    });
  } catch {
    throw new ApiError(0, "network_error", "Sem conexão com o servidor. Tente de novo.");
  }
  if (!res.ok || !res.body) throw await toApiError(res);
  const feed = createSseParser((event, data) => {
    const e = toChatEvent(event, data);
    if (e) onEvent(e);
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    feed(decoder.decode(value, { stream: true }));
  }
  feed(`${decoder.decode()}\n\n`);
}

export const confirmAssistantAction = (wid: string, id: string, patch?: Record<string, unknown>) =>
  api.post<{ action: AssistantAction }>(
    `/workspaces/${wid}/assistant/actions/${id}/confirm`,
    patch && Object.keys(patch).length ? { patch } : {},
  );

export const cancelAssistantAction = (wid: string, id: string) =>
  api.post<{ action: AssistantAction }>(`/workspaces/${wid}/assistant/actions/${id}/cancel`);

export const getAssistantAction = (wid: string, id: string) =>
  api.get<{ action: AssistantAction }>(`/workspaces/${wid}/assistant/actions/${id}`);

/** Chamada de voz aberta pelo servidor (interface unificada): só a resposta SDP, nenhuma credencial. */
export interface VoiceSession {
  answerSdp: string;
  callId: string;
  model: string;
  remainingSeconds: number;
  heartbeatSeconds: number;
}

/** Resultado de uma ferramenta pedida pelo modelo na voz: `output` volta ao modelo; o cartão, à tela. */
export interface VoiceToolResult {
  output: unknown;
  proposal?: AssistantAction;
  action?: AssistantAction;
}

async function postJson<T>(
  path: string,
  body: unknown,
  init: { signal?: AbortSignal; keepalive?: boolean } = {},
) {
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
      ...init,
    });
  } catch {
    throw new ApiError(0, "network_error", "Sem conexão com o servidor. Tente de novo.");
  }
  if (!res.ok) throw await toApiError(res);
  return (await res.json().catch(() => null)) as T;
}

/** Abre a chamada: manda a oferta SDP e recebe a resposta SDP (o áudio vai direto à OpenAI). */
export const startVoiceSession = (wid: string, sdp: string, signal?: AbortSignal) =>
  postJson<VoiceSession>(`/workspaces/${wid}/assistant/voice/session`, { sdp }, { signal });

export const voiceHeartbeat = (wid: string, callId: string) =>
  postJson<{ remainingSeconds: number }>(`/workspaces/${wid}/assistant/voice/heartbeat`, { callId });

/** Encerra a chamada; no `pagehide`, com `keepalive` para a requisição sobreviver ao fechamento da aba. */
export const endVoiceCall = async (wid: string, callId: string, opts: { keepalive?: boolean } = {}) => {
  await postJson<{ ok: boolean }>(`/workspaces/${wid}/assistant/voice/end`, { callId }, opts);
};

export const runVoiceTool = (wid: string, name: string, args: Record<string, unknown>) =>
  postJson<VoiceToolResult>(`/workspaces/${wid}/assistant/tools/${encodeURIComponent(name)}`, {
    arguments: args,
  });
